import Api from '../../Api';
import { IAlertMetricsDto } from '../../shared-interfaces/Notation/Alert/IAlertMetricsDto';
import { IAlertsOvertimeDto } from '../../shared-interfaces/Notation/Alert/IAlertsOvertimeDto';
import { ICreateAlertRequest } from '../../shared-interfaces/Notation/Alert/ICreateAlertRequest';
import { IMultiStudentCreateAlertRequest } from '../../shared-interfaces/Notation/Alert/IMultiStudentCreateAlertRequest';
import { IAction } from '../../shared-interfaces/Notation/IAction';
import { IMethodOfContact } from '../../shared-interfaces/Notation/IMethodOfContact';
import { INotation } from '../../shared-interfaces/Notation/INotation';
import { INotationComment } from '../../shared-interfaces/Notation/INotationComment';
import { ICoreUser } from '../../shared-interfaces/User/ICoreUser';
import AudienceType from '../../shared-types/AudienceType';
import { Inclusion } from '../../shared-types/Inclusion';

// export interface IBulkCreateAlertRequest {
//   details: string;
//   studentIds: string[];
//   canStudentView: boolean;
//   audienceType: AudienceType;
//   institutionId: number;
//   audienceMembers: string[];
//   additionalAudienceMembers: string[];
//   selectedCategoryIds: number[];
//   selectedReasonIds: number[];
//   occurenceDate: Date;
//   assignedToId: string;
// }

export interface IBulkCreateAlertResponse {
  successful: INotation[];
  failed: string[];
}

const AlertService = {
  create: async (request: ICreateAlertRequest) => {
    await Api.post(`notation/alert`, request);
  },
  createMultiple: async (request: IMultiStudentCreateAlertRequest) => {
    const response = await Api.post(`notation/alert/multiple`, request);
    return response.data as INotation[];
  },
  bulkCreateAlert: async (request: IMultiStudentCreateAlertRequest) => {
    const response = await Api.post('notation/alert/bulk', request);

    return response.data as IBulkCreateAlertResponse;
  },
  getComments: async (id: number) => {
    const response = await Api.get(`notation/alert/${id}/comments`);
    return response.data.map((x: any) => ({
      ...x,
      createdOn: new Date(x.createdOn),
      modifiedOn: new Date(x.modifiedOn),
    })) as INotationComment[];
  },
  addComment: async (
    id: number,
    action: IAction,
    details: string,
    assignedTo?: ICoreUser,
    additionalAudience?: ICoreUser[],
    methodOfContact?: IMethodOfContact,
  ) => {
    await Api.post(`notation/alert/${id}/comment`, {
      actionId: action.id,
      details: details,
      methodOfContactId: methodOfContact?.id,
      additionalAudience: additionalAudience?.map(user => {
        return user.id;
      }),
      assignedToId: assignedTo?.id,
    });
  },
  lower: async (
    id: number,
    actionId: number,
    details: string,
    methodOfContactId?: number,
    assignedToId?: string | undefined,
  ): Promise<boolean> => {
    const response = await Api.post(`notation/alert/${id}/lower`, {
      actionId: actionId,
      details: details,
      methodOfContactId: methodOfContactId,
      assignedToId: assignedToId,
    });
    return response.status === 200;
  },
  bulkLower: async (
    actionId: number,
    details: string,
    restrictTo: Inclusion,
    methodOfContactId?: number,
    categoryIds?: number[],
    createdBy?: string,
    createdFor?: string,
    startDate?: Date,
    endDate?: Date,
    ids?: number[],
  ): Promise<boolean> => {
    const response = await Api.post(`notation/alert/lower`, {
      actionId,
      details,
      methodOfContactId,
      categoryIds,
      createdBy,
      createdFor,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      restrictTo: restrictTo,
      ids: ids,
    });
    return response.status === 200;
  },
  delete: async (id: number) => {
    await Api.delete(`notation/alert/${id}`);
  },
  deleteComment: async (notationId: number, commentId: number) => {
    await Api.delete(`notation/alert/${notationId}/comment/${commentId}`);
  },
  alertsOvertime: async (
    startDate: Date,
    endDate: Date,
  ): Promise<IAlertsOvertimeDto> => {
    const response = await Api.get('notation/alert/overtime', {
      startDate: startDate,
      endDate: endDate,
    });
    if (response.status !== 200) throw response.status;
    return {
      ...response.data,
      startingTime: new Date(response.data.startingTime),
    };
  },
  alertMetrics: async (
    startDate?: Date,
    endDate?: Date,
  ): Promise<IAlertMetricsDto> => {
    const response = await Api.get('notation/alert/metrics', {
      startDate: startDate,
      endDate: endDate,
    });
    if (response.status !== 200) throw response.status;
    return response.data;
  },
};

export default AlertService;
